<template>
    <div
        class="sub-menu-section"
        :class="classSubMenuSection"
    >
        <span
            v-if="section.title"
            class="title"
            :role="section.title === ' ' ? 'none' : undefined"
        >
            {{ section.title }}
        </span>

        <ul
            v-if="section.items.length"
            class="list"
        >
            <li
                v-for="(item, index) in section.items"
                :key="index"
                class="item"
                :class="getClassSubMenuItem(item)"
            >
                <HeaderLink
                    :link="item.link"
                    class="link"
                    @click="close"
                >
                    <div
                        v-if="item.media"
                        class="image"
                    >
                        <CmsImage
                            :data="item.media"
                            fit="contain"
                            :sizes="
                                useLargeFormatItems
                                    ? ['90px', [360, '160px'], [768, '220px']]
                                    : ['100vw']
                            "
                        />
                    </div>

                    <div
                        v-if="item.icon"
                        class="icon"
                    >
                        <CmsImage
                            :data="item.icon"
                            fit="contain"
                            :sizes="['30px']"
                        />
                    </div>

                    <span
                        class="item-title"
                        :role="!item.title ? 'none' : undefined"
                    >
                        {{ item.title }}
                    </span>
                </HeaderLink>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import type { SubMenuItemFragment, SubMenuSectionFragment } from '@/graphql/api'

import HeaderLink from '@/components/layout/header/menu/HeaderLink.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import { computed } from 'vue'

const { section, useLargeFormatItems } = defineProps<{
    section: SubMenuSectionFragment
    useLargeFormatItems?: boolean
}>()

const emit = defineEmits<{
    close: []
}>()

const close = () => emit('close')

const classSubMenuSection = computed(() => ({
    'no-title': section.title === ' ',
}))

const getClassSubMenuItem = (item: SubMenuItemFragment) => ({
    'with-images-only': useLargeFormatItems,
    'with-icon': !!item.icon,
    [item.style.toLocaleLowerCase()]: true,
    'empty': item.title === ' ',
})
</script>

<style lang="scss" scoped>
.sub-menu-section {
    --title-height: 36px;
    --padding-bottom: 30px;

    width: 100%;
    padding-bottom: var(--padding-bottom);

    color: $slater;
    font-family: $effra-font-stack;
    letter-spacing: 0.05em;
    font-weight: 400;
    text-transform: uppercase;
    font-size: 0.875rem;
    line-height: 1.25rem;

    @include min-width(1024) {
        width: 25%;
        height: unset;
    }

    &.no-title {
        margin-top: calc(var(--padding-bottom) * -1);

        @include min-width(1024) {
            margin-top: 0;
        }

        .title {
            height: 0;
            padding: 0;

            @include min-width(1024) {
                height: var(--title-height);
            }
        }
    }

    &:nth-of-type(2n) {
        .list {
            .item {
                &.with-images-only {
                    :deep(.link) {
                        flex-direction: row-reverse;
                    }
                }
            }
        }
    }

    .title {
        height: var(--title-height);
        display: block;
        padding: 10px 36px;

        font-weight: 500;
        font-size: 0.95rem;
        color: $slater;

        @include min-width(1024) {
            font-size: 0.875rem;
            line-height: 1.2rem;
            padding: 8px 0;
        }
    }

    .list {
        .item {
            color: $slater;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-weight: 400;
            text-transform: uppercase;
            font-size: 0.875rem;
            line-height: 1.25rem;
            padding-left: 36px;
            height: 40px;
            display: flex;
            align-items: center;

            @include min-width(1024) {
                padding: 0;
                height: 36px;
            }

            &:not(.with-images-only) {
                :deep(.link) {
                    display: block;
                    font-size: 0.875rem;
                    color: $slater;

                    @include min-width(1024) {
                        font-size: 0.75rem;
                        padding: 6px 0;
                    }

                    @include hover {
                        color: $green;
                    }
                }

                &.large {
                    :deep(.link) {
                        color: $slater;
                        font-size: 0.95rem;

                        font-weight: 500;

                        @include min-width(1024) {
                            font-size: 0.875rem;
                        }

                        @include min-width(1024) {
                            font-weight: 500;
                            color: $slater;
                        }
                    }
                }
            }

            &.empty {
                height: 0px;
            }

            &.with-icon {
                :deep(.link) {
                    display: flex;
                    align-items: center;
                }

                .icon {
                    width: 30px;
                    height: 26px;
                    margin-right: 8px;
                    display: flex;
                    align-items: center;
                }
            }

            &.with-images-only {
                width: 100%;
                flex-shrink: 0;
                margin: 0 0 10px;

                .link {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    font-size: 0.875rem;
                    align-items: center;
                    text-align: center;

                    @include min-width(768) {
                        flex-wrap: wrap;
                    }
                }

                .image {
                    display: block;
                    min-width: 90px;
                    height: 140px;
                    border-radius: 50px;
                    background-repeat: no-repeat;
                    background-size: cover;
                    background-position: center center;
                    margin: 0 0 20px;
                    overflow: hidden;

                    @include min-width(360) {
                        min-width: 160px;
                        height: 228px;
                        border-radius: 80px;
                        margin: 0 0 20px;
                    }

                    @include min-width(768) {
                        width: 220px;
                        height: 300px;
                        border-radius: 210px;
                        flex-shrink: 0;
                    }
                }

                @include hover {
                    .item-title::after {
                        @include min-width(768) {
                            padding-left: 5px;
                        }
                    }
                }

                .item-title {
                    padding: 24px;
                    text-align: center;
                    width: 100%;
                    display: block;
                    font-size: 0.95rem;
                    font-weight: 500;

                    @include min-width(768) {
                        width: 100%;
                        margin-top: 20px;
                        padding: 0;
                    }
                }
            }
        }
    }
}
</style>
